// LearnCore Frontend - 创建任务表单组件

import React, { useState, useEffect } from 'react';
import { tasksApi } from '../api/tasks';
import type { 
  TaskTemplate,
  CreateTaskRequest,
  TaskCategory
} from '../types/tasks-rewards';

interface CreateTaskFormProps {
  userId?: string;
  onSuccess?: (task: any) => void;
  onCancel?: () => void;
  initialData?: Partial<CreateTaskRequest>;
  showTemplateSelection?: boolean;
  className?: string;
}

export const CreateTaskForm: React.FC<CreateTaskFormProps> = ({
  userId,
  onSuccess,
  onCancel,
  initialData,
  showTemplateSelection = true,
  className = ''
}) => {
  const [templates, setTemplates] = useState<TaskTemplate[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState<TaskTemplate | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [useTemplate, setUseTemplate] = useState(showTemplateSelection);

  const [formData, setFormData] = useState<CreateTaskRequest>({
    user_id: userId,
    title: '',
    description: '',
    category: 'custom',
    type: 'custom',
    target_value: 1,
    dkp_reward: 10,
    priority: 'medium',
    due_date: undefined,
    ...initialData
  });

  useEffect(() => {
    if (showTemplateSelection) {
      loadTemplates();
    }
  }, [showTemplateSelection]);

  useEffect(() => {
    if (selectedTemplate) {
      setFormData(prev => ({
        ...prev,
        title: selectedTemplate.title,
        description: selectedTemplate.description,
        category: selectedTemplate.category,
        type: selectedTemplate.type,
        target_value: selectedTemplate.target_value,
        dkp_reward: selectedTemplate.dkp_reward,
        priority: selectedTemplate.priority
      }));
    }
  }, [selectedTemplate]);

  const loadTemplates = async () => {
    try {
      const response = await tasksApi.getTaskTemplates();
      setTemplates(response.templates || []);
    } catch (err) {
      console.error('Failed to load templates:', err);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.title.trim()) {
      setError('请输入任务标题');
      return;
    }

    if (formData.target_value <= 0) {
      setError('目标值必须大于0');
      return;
    }

    if (formData.dkp_reward < 0) {
      setError('金币奖励不能为负数');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const response = await tasksApi.createTask(formData);
      
      if (onSuccess) {
        onSuccess(response.task);
      }
    } catch (err: any) {
      setError(err.response?.data?.error || '创建任务失败');
      console.error('Failed to create task:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: keyof CreateTaskRequest, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setError(null);
  };

  const getCategoryText = (category: TaskCategory): string => {
    switch (category) {
      case 'study_time': return '学习时间';
      case 'cards_completed': return '完成卡片';
      case 'accuracy_rate': return '正确率';
      case 'consecutive_days': return '连续天数';
      case 'total_mastered': return '掌握词汇';
      case 'content_created': return '创建内容';
      case 'custom': return '自定义';
      default: return category;
    }
  };

  const getTargetUnit = (category: TaskCategory): string => {
    switch (category) {
      case 'study_time': return '分钟';
      case 'cards_completed': return '个';
      case 'accuracy_rate': return '%';
      case 'consecutive_days': return '天';
      case 'total_mastered': return '个';
      case 'content_created': return '个';
      default: return '个';
    }
  };

  return (
    <div className={`bg-white rounded-lg shadow-sm border border-gray-200 p-6 ${className}`}>
      <h2 className="text-lg font-semibold text-gray-900 mb-4">
        {initialData ? '编辑任务' : '创建新任务'}
      </h2>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-800 text-sm">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-4">
        {/* 模板选择 */}
        {showTemplateSelection && (
          <div>
            <label className="flex items-center gap-2 mb-2">
              <input
                type="checkbox"
                checked={useTemplate}
                onChange={(e) => setUseTemplate(e.target.checked)}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="text-sm font-medium text-gray-700">从模板创建</span>
            </label>
            
            {useTemplate && (
              <div className="space-y-2">
                <select
                  value={selectedTemplate?.id || ''}
                  onChange={(e) => {
                    const template = templates.find(t => t.id === e.target.value);
                    setSelectedTemplate(template || null);
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">选择模板...</option>
                  {templates.map((template) => (
                    <option key={template.id} value={template.id}>
                      {template.title} ({getCategoryText(template.category)} - +{template.dkp_reward} 金币)
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>
        )}

        {/* 任务标题 */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            任务标题 *
          </label>
          <input
            type="text"
            value={formData.title}
            onChange={(e) => handleInputChange('title', e.target.value)}
            placeholder="输入任务标题..."
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            required
          />
        </div>

        {/* 任务描述 */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            任务描述
          </label>
          <textarea
            value={formData.description || ''}
            onChange={(e) => handleInputChange('description', e.target.value)}
            placeholder="输入任务描述..."
            rows={3}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* 任务分类 */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              任务分类
            </label>
            <select
              value={formData.category}
              onChange={(e) => handleInputChange('category', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="custom">自定义</option>
              <option value="study_time">学习时间</option>
              <option value="cards_completed">完成卡片</option>
              <option value="accuracy_rate">正确率</option>
              <option value="consecutive_days">连续天数</option>
              <option value="total_mastered">掌握词汇</option>
              <option value="content_created">创建内容</option>
            </select>
          </div>

          {/* 任务类型 */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              任务类型
            </label>
            <select
              value={formData.type}
              onChange={(e) => handleInputChange('type', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="custom">自定义任务</option>
              <option value="daily">日常任务</option>
              <option value="habit">习惯任务</option>
              <option value="achievement">成就任务</option>
            </select>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* 目标值 */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              目标值 *
            </label>
            <div className="flex">
              <input
                type="number"
                min="1"
                value={formData.target_value}
                onChange={(e) => handleInputChange('target_value', parseInt(e.target.value) || 1)}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-l-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
              <span className="px-3 py-2 bg-gray-50 border border-l-0 border-gray-300 rounded-r-lg text-gray-600">
                {getTargetUnit(formData.category)}
              </span>
            </div>
          </div>

          {/* 金币奖励 */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              金币奖励 *
            </label>
            <div className="flex">
              <input
                type="number"
                min="0"
                value={formData.dkp_reward}
                onChange={(e) => handleInputChange('dkp_reward', parseInt(e.target.value) || 0)}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-l-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
              <span className="px-3 py-2 bg-gray-50 border border-l-0 border-gray-300 rounded-r-lg text-gray-600">
                金币
              </span>
            </div>
          </div>

          {/* 优先级 */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              优先级
            </label>
            <select
              value={formData.priority}
              onChange={(e) => handleInputChange('priority', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="low">低</option>
              <option value="medium">中</option>
              <option value="high">高</option>
            </select>
          </div>
        </div>

        {/* 截止日期 */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            截止日期
          </label>
          <input
            type="datetime-local"
            value={formData.due_date ? new Date(formData.due_date).toISOString().slice(0, 16) : ''}
            onChange={(e) => {
              const date = e.target.value ? new Date(e.target.value).getTime() : undefined;
              handleInputChange('due_date', date);
            }}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>

        {/* 按钮组 */}
        <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
          {onCancel && (
            <button
              type="button"
              onClick={onCancel}
              className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
            >
              取消
            </button>
          )}
          <button
            type="submit"
            disabled={loading}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 transition-colors"
          >
            {loading ? '创建中...' : initialData ? '更新任务' : '创建任务'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default CreateTaskForm;